import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Image } from './image';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true,
    },
  ],
})
export class ImageUploadComponent implements ControlValueAccessor, Validator {
  @Input() multiple = false;
  public imageForm = this.fb.group({
    image: new FormControl<File[]>([], {
      nonNullable: true,
    }),
    images: new FormControl<File[]>([], {
      nonNullable: true,
    }),
  });
  imagePreview: string | undefined = undefined;
  imageFile: File | undefined = undefined;
  imageFiles: File[] = [];
  images: Image[] = [];
  imageString = '';
  addImageLabel = 'Ajouter une image(1Mo max)';
  addImagesLabel = 'Ajouter des images(1Mo max)';
  imageTooBigLabel = 'Image trop grande';
  private readonly MAX_FILE_SIZE: number = 1400000;

  onChange: (file: string | Image[]) => void = () => {};

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private fb: FormBuilder
  ) {}

  @HostListener('change', ['$event.target.files']) emitFiles(
    files: FileList
  ): void {
    if (files) {
      if (this.multiple) {
        this.imageFiles = Array.from(files);
        for (const file of this.imageFiles) {
          const reader = new FileReader();
          reader.onload = this.ImageFileToBase64();
          reader.readAsDataURL(file);
        }
      } else {
        this.imageFile = Array.from(files)[0];
        const reader = new FileReader();
        reader.onload = this.ImageFileToBase64();
        reader.readAsDataURL(this.imageFile);
      }
    }
  }

  writeValue(value: string | Image[]): void {
    this.host.nativeElement.value = '';
    this.imageFile = undefined;
    this.imagePreview = undefined;
    this.imageFiles = [];
    this.images = [];
    if (value) {
      if (typeof value === 'string') {
        this.imagePreview = `data:image/jpg;base64,${value}`;
      }
      if (Array.isArray(value)) {
        value.forEach((image: Image) => this.images.push(image));
      }
    } else {
      this.imageForm.reset();
    }
  }

  registerOnChange(fn: (files: string | Image[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {}

  validate(c: AbstractControl): ValidationErrors | null {
    return this.imageForm.valid
      ? null
      : {
          invalidForm: {
            valid: false,
            message: 'imageForm fields are invalid',
          },
        };
  }

  remove(imageToRemove: Image): void {
    this.images = [
      ...this.images.filter(
        (image) =>
          !image.data.includes(imageToRemove.data) ||
          !image.location.includes(imageToRemove.location)
      ),
    ];
    this.onChange(this.images);
  }

  private ImageFileToBase64(): (e: ProgressEvent<FileReader>) => void {
    return (e: ProgressEvent<FileReader>) => {
      const imageData = this.getImageBase64String(e);
      if (this.imageIsSmallerThanMaxSize(imageData)) {
        this.propagateImageData(imageData);
      } else {
        this.sizeError();
      }
    };
  }

  private sizeError(): void {
    if (this.multiple) {
      this.imageForm.controls.images.setErrors({ incorrect: true });
    } else {
      this.imageForm.controls.image.setErrors({ incorrect: true });
    }

    return;
  }

  private propagateImageData(imageData: string): void {
    if (this.multiple) {
      this.images.push({ location: imageData, data: imageData.split(',')[1] });
      this.onChange(this.images);
    } else {
      this.imageString = imageData.split(',')[1];
      this.imagePreview = imageData;
      this.onChange(this.imageString);
    }
  }

  private imageIsSmallerThanMaxSize(imageData: string): boolean {
    return imageData.length <= this.MAX_FILE_SIZE;
  }

  private getImageBase64String(e: ProgressEvent<FileReader>): string {
    return e?.target?.result as string;
  }
}
